import React from 'react';
import { motion } from 'framer-motion';
import { SUPPORT_PHONE } from '../config/contact';

const RefundPage = () => {
  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-5xl font-bold mb-6">Refund & Return Policy</h1>
          <p className="text-gray-500">Last updated: January 2025</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="prose prose-invert max-w-none"
        >
          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Return Window</h2>
              <p className="text-gray-400 leading-relaxed">
                We offer a 30-day return window from the date of delivery. If 30 days have passed since your purchase, we cannot offer you a refund or exchange.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Eligibility for Returns</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                To be eligible for a return, your item must be:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
                <li>Unused and in the same condition that you received it</li>
                <li>In its original packaging with all accessories and documentation</li>
                <li>Accompanied by proof of purchase (order confirmation email)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. Non-Returnable Items</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                The following items cannot be returned:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
                <li>Products damaged due to misuse or neglect</li>
                <li>Products with tampered or damaged serial numbers</li>
                <li>Products without original packaging</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Return Process</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                To initiate a return:
              </p>
              <ol className="list-decimal list-inside text-gray-400 space-y-3 ml-4">
                <li>Contact our customer service at support@clearway-mobility.com with your order number</li>
                <li>Provide reason for return and photos of the product (if applicable)</li>
                <li>Wait for return authorization and shipping instructions</li>
                <li>Pack the item securely in its original packaging</li>
                <li>Ship the item to the address provided by our team</li>
              </ol>
              <p className="text-gray-400 leading-relaxed mt-4">
                <strong>Note:</strong> Return shipping costs are the responsibility of the customer unless the product is defective or damaged upon arrival.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Refund Processing</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Once we receive your return, we will:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
                <li>Inspect the item within 3-5 business days</li>
                <li>Send you an email notification of approval or rejection</li>
                <li>Process approved refunds within 7-10 business days</li>
                <li>Refund to the original payment method used for purchase</li>
              </ul>
              <p className="text-gray-400 leading-relaxed mt-4">
                Original shipping charges are non-refundable (unless product was defective or incorrect).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Exchanges</h2>
              <p className="text-gray-400 leading-relaxed">
                We only replace items if they are defective or damaged. If you need to exchange a product for the same item, contact us at support@clearway-mobility.com with your order number.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Defective or Damaged Products</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                If your product arrives defective or damaged:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
                <li>Contact us within 48 hours of delivery</li>
                <li>Provide photos of the damage or defect</li>
                <li>We will arrange free return shipping</li>
                <li>Full refund or replacement will be provided</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Cancellations</h2>
              <p className="text-gray-400 leading-relaxed">
                Orders can be cancelled within 24 hours of placement for a full refund. After 24 hours, if the order has been processed or shipped, standard return policies apply.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. International Returns</h2>
              <p className="text-gray-400 leading-relaxed">
                For international orders (UAE), return shipping costs are higher. Please contact us before initiating a return to discuss the best solution. Import duties and taxes are non-refundable.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Warranty Claims</h2>
              <p className="text-gray-400 leading-relaxed">
                Warranty claims are handled separately from returns. Please refer to your warranty card or contact support@clearway-mobility.com for warranty-related issues.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Contact for Returns</h2>
              <p className="text-gray-400 leading-relaxed">
                For all return and refund inquiries, please contact:
                <br /><br />
                Email: support@clearway-mobility.com<br />
                Phone: {SUPPORT_PHONE}<br />
                Subject: Return Request - Order #[Your Order Number]<br />
                <br />
                We aim to respond to all return requests within 24-48 hours.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RefundPage;
